import React, { useEffect, useState } from "react";
import { X, Info, Send, Layers, Map as MapIcon, Image as ImageIcon } from "lucide-react";
import HotspotPlan from "../components/site/HotspotPlan";
import AreaMapView from "../components/site/AreaMapView";
import EnquiryDialog from "../components/site/EnquiryDialog";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";

const MasterPlan = () => {
  const [stages, setStages] = useState([]);
  const [project, setProject] = useState({});
  const [view, setView] = useState("plan");
  const [selStage, setSelStage] = useState(null);
  const [cardPos, setCardPos] = useState(null);
  const [stageEnq, setStageEnq] = useState(null);
  const navigate = useNavigate();

  const stageColor = (s) => s.status === "available" ? "#C9B79C" : s.status === "completed" ? "#3E7A5E" : "#5B6B7A";
  const stagePins = stages.filter((s) => s.hotspot && typeof s.hotspot.x === "number").map((s) => ({
    id: s.id, x: s.hotspot.x, y: s.hotspot.y, label: String(s.number), color: stageColor(s), title: `${s.name} · ${s.status_label || ""}`, stage: s,
  }));
  const COMMERCIAL_PIN = { id: "commercial", x: 0.26, y: 0.599, label: "C", color: "#E8A0B0", title: "Commercial Centre — Stage 7 Mixed Use Node", commercial: true };
  const planPins = [...stagePins, COMMERCIAL_PIN];
  const handlePlanPin = (pin, el) => {
    if (pin.commercial) { navigate("/commercial"); return; }
    setSelStage(pin.stage);
    if (el && el.getBoundingClientRect) {
      const r = el.getBoundingClientRect();
      setCardPos({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    } else {
      setCardPos(null);
    }
  };

  const ZONES = [
    ["#E9C46A", "Low–Medium Density Residential"],
    ["#E8843A", "Medium–High Density Residential"],
    ["#B8862B", "Medium Density Residential"],
    ["#6B3A1E", "High Density Residential"],
    ["#B4232A", "High Street Commercial"],
    ["#3E7A2E", "Parks & Open Space"],
    ["#7FB2D9", "Stormwater Ponds"],
  ];

  useEffect(() => {
    api.get("/stages").then((r) => setStages(r.data)).catch(() => {});
    api.get("/project").then((r) => setProject(r.data || {})).catch(() => {});
  }, []);



  return (
    <div className="pt-[64px] h-[100svh] flex flex-col bg-[#12201A]">
      <div className="flex flex-1 min-h-0">
        {/* sidebar */}
        <aside className="w-[320px] shrink-0 border-r border-[#ffffff14] bg-[#0F1B15] overflow-y-auto hidden md:block">
          <div className="p-6">
            <p className="eyebrow text-[#C9B79C] mb-1">Interactive</p>
            <h1 className="font-display text-2xl text-[#F3EEE3]">Master Plan</h1>

            <div className="mt-5 flex flex-wrap gap-2 p-1 rounded-2xl bg-[#0C1510] border border-[#F3EEE3]/12">
              <button onClick={() => setView("plan")} data-testid="view-plan" className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs py-2 px-2 rounded-xl transition-colors ${view === "plan" ? "bg-[#C9B79C] text-[#12201A]" : "text-[#F3EEE3]/60"}`}><ImageIcon size={13} /> Site Plan</button>
              {project.area_map_image && (
                <button onClick={() => setView("area")} data-testid="view-area" className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs py-2 px-2 rounded-xl transition-colors ${view === "area" ? "bg-[#C9B79C] text-[#12201A]" : "text-[#F3EEE3]/60"}`}><MapIcon size={13} /> Area Map</button>
              )}
            </div>

            {view === "plan" && (
              <div className="mt-6" data-testid="plan-zone-legend">
                <p className="eyebrow text-[0.55rem] text-[#F3EEE3]/45 mb-3 flex items-center gap-2"><Layers size={12} /> Map legend</p>
                <div className="space-y-2">
                  {ZONES.map(([c, l]) => (
                    <div key={l} className="flex items-center gap-2.5 text-xs text-[#F3EEE3]/70">
                      <span className="w-3.5 h-3.5 rounded-sm border border-[#F3EEE3]/20" style={{ background: c }} /> {l}
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-xl border border-[#C9B79C]/25 bg-[#C9B79C]/10 p-3">
                  <p className="text-[#F3EEE3]/75 text-xs leading-relaxed">Numbered pins mark the development stages shown on the developer's plan (Stages 7–11). Tap a pin to open a stage, or tap <span className="text-[#E8A0B0]">C</span> for the Commercial Centre.</p>
                </div>
              </div>
            )}
            <p className="text-[0.62rem] text-[#F3EEE3]/35 mt-8 leading-relaxed">
              {view === "area"
                ? "City of Spruce Grove parks & recreation map. Scroll to zoom, drag to pan. Source: City of Spruce Grove."
                : "Official Deer Park Estates site plan. Scroll to zoom, drag to pan. Figures are preliminary and subject to approval."}
            </p>
          </div>
        </aside>

        {/* map */}
        <div className="relative flex-1 min-w-0">
          {view === "area" ? (
            <div className="h-full w-full overflow-auto p-3 md:p-4">
              {project.area_map_image ? (
                <AreaMapView mapSrc={project.area_map_image} infoSrc={project.area_info_image} panelHeight="lg:max-h-[70vh]" />
              ) : (
                <div className="h-full flex items-center justify-center text-[#F3EEE3]/40 text-sm">Area map coming soon.</div>
              )}
            </div>
          ) : (
            <div className="h-full w-full p-3 md:p-4 overflow-auto">
              {project.site_plan_image ? (
                <HotspotPlan src={project.site_plan_image} pins={planPins} onPinClick={handlePlanPin} fit maxHeight="calc(100svh - 96px)" />
              ) : (
                <div className="h-full flex items-center justify-center text-[#F3EEE3]/40 text-sm">Site plan coming soon.</div>
              )}
            </div>
          )}

          {/* mobile view toggle */}
          <div className="md:hidden absolute top-4 left-4 z-10 flex gap-1.5 p-1 rounded-full bg-[#12201A]/85 backdrop-blur border border-[#F3EEE3]/15">
            <button onClick={() => setView("plan")} className={`px-2.5 py-1.5 rounded-full text-xs ${view === "plan" ? "bg-[#C9B79C] text-[#12201A]" : "text-[#F3EEE3]/70"}`}>Site Plan</button>
            {project.area_map_image && <button onClick={() => setView("area")} className={`px-2.5 py-1.5 rounded-full text-xs ${view === "area" ? "bg-[#C9B79C] text-[#12201A]" : "text-[#F3EEE3]/70"}`}>Area</button>}
          </div>

          {/* stage card (site plan pins) — pops up near the clicked pin */}
          {view === "plan" && selStage && (() => {
            const CARD_W = 320;
            const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
            const vh = typeof window !== "undefined" ? window.innerHeight : 800;
            const px = cardPos?.x ?? vw - 180;
            const py = cardPos?.y ?? 140;
            const left = px > vw / 2 ? Math.max(px - CARD_W - 22, 8) : Math.min(px + 22, vw - CARD_W - 8);
            const top = Math.min(Math.max(py - 60, 76), vh - 300);
            return (
            <div style={{ position: "fixed", left, top, width: CARD_W }} className="bg-[#12201A]/95 backdrop-blur border border-[#F3EEE3]/15 rounded-2xl p-6 z-50 shadow-[0_12px_44px_rgba(0,0,0,0.6)]" data-testid="stage-card">
              <button onClick={() => { setSelStage(null); setCardPos(null); }} className="absolute top-4 right-4 text-[#F3EEE3]/60 hover:text-[#F3EEE3]"><X size={18} /></button>
              <span className="inline-block px-2.5 py-1 rounded-full text-[0.6rem] tracking-wide" style={{ background: stageColor(selStage) + "33", color: "#F3EEE3", border: `1px solid ${stageColor(selStage)}` }}>
                {selStage.status_label}
              </span>
              <h3 className="font-display text-2xl text-[#F3EEE3] mt-3">{selStage.name}</h3>
              {selStage.parcels_count && <p className="text-[#F3EEE3]/55 text-sm mt-1">{selStage.parcels_count} lots</p>}
              {selStage.description && <p className="text-[#F3EEE3]/65 text-sm mt-3 leading-relaxed">{selStage.description}</p>}
              <div className="flex gap-2 mt-5">
                <button onClick={() => navigate(`/stages/${selStage.id}`)} data-testid="stage-card-view" className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-4 py-2.5 rounded-full text-sm font-medium hover:bg-white">View stage</button>
                <button onClick={() => setStageEnq(selStage)} data-testid="stage-card-enquire" className="flex-1 inline-flex items-center justify-center gap-2 border border-[#F3EEE3]/30 text-[#F3EEE3] px-4 py-2.5 rounded-full text-sm hover:bg-[#F3EEE3]/10"><Send size={14} /> Enquire</button>
              </div>
            </div>
            );
          })()}

          <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2 text-[0.65rem] text-[#F3EEE3]/70 bg-[#12201A]/70 backdrop-blur rounded-full px-3.5 py-2">
            <Info size={13} /> {view === "area" ? "City of Spruce Grove area map — zoom & pan" : "Official site plan — zoom & pan to explore"}
          </div>
        </div>
      </div>

      <EnquiryDialog open={!!stageEnq} onClose={() => setStageEnq(null)} context={{ stage_id: stageEnq?.id, label: stageEnq?.name, source: "master-plan-stage" }} />
    </div>
  );
};

export default MasterPlan;
